/**
 * Route Configuration
 */
import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { AdminLayout } from '@presentation/layouts'
import { Loading } from '@presentation/components'
import { DevAdminGuard } from '@presentation/components/auth'
import { ROUTES } from '@shared/constants'

// Lazy load pages
const DashboardPage = lazy(() => import('@features/dashboard/DashboardPage'))
const HealthPage = lazy(() => import('@features/health/HealthPage'))
const WorkflowListPage = lazy(() => import('@features/workflows/WorkflowListPage'))
const WorkflowDetailsPage = lazy(() => import('@features/workflows/WorkflowDetailsPage'))
const ExecutionListPage = lazy(() => import('@features/executions/ExecutionListPage'))
const ExecutionDetailsPage = lazy(() => import('@features/executions/ExecutionDetailsPage'))
const MonitoringPage = lazy(() => import('@features/monitoring/MonitoringPage'))
const TemplateListPage = lazy(() => import('@features/templates/TemplateListPage'))
const TemplateDetailsPage = lazy(() => import('@features/templates/TemplateDetailsPage'))
const GroupListPage = lazy(() => import('@features/groups/GroupListPage'))
const GroupDetailsPage = lazy(() => import('@features/groups/GroupDetailsPage'))
const TenantListPage = lazy(() => import('@features/tenants/TenantListPage'))
const IdentitySwitchOverPage = lazy(() => import('@features/identity-switch-over/IdentitySwitchOverPage'))
const PersonExportPage = lazy(() => import('@features/person-export/PersonExportPage'))
const UserListPage = lazy(() => import('@features/users/UserListPage'))
const ConnectorListPage = lazy(() => import('@features/connectors/ConnectorListPage'))
const ConnectorDetailsPage = lazy(() => import('@features/connectors/ConnectorDetailsPage'))
const InfraOverviewPage = lazy(() => import('@features/infra/InfraOverviewPage'))
const RedisPage = lazy(() => import('@features/infra/RedisPage'))
const ServiceBusPage = lazy(() => import('@features/infra/ServiceBusPage'))
const DbBrowserPage = lazy(() => import('@features/infra/DbBrowserPage'))
const DbTablePage = lazy(() => import('@features/infra/DbTablePage'))
const DeploymentPage = lazy(() => import('@features/deployment/DeploymentPage'))
const LiveTestsPage = lazy(() => import('@features/live-tests/LiveTestsPage'))
const LiveTestHistoryPage = lazy(() => import('@features/live-tests/LiveTestHistoryPage'))
const LiveTestRunPage = lazy(() => import('@features/live-tests/LiveTestRunPage'))
const AuditLogPage = lazy(() => import('@features/audit/AuditLogPage'))
const PresetListPage = lazy(() => import('@features/presentation/PresetListPage'))
const PresetEditorPage = lazy(() => import('@features/presentation/PresetEditorPage'))
const WhatsNewPage = lazy(() => import('@presentation/pages/WhatsNewPage'))
const NotFoundPage = lazy(() => import('@presentation/pages/NotFoundPage'))
const RestingPage = lazy(() => import('@presentation/pages/auth/RestingPage'))

function withSuspense(Component: React.ComponentType) {
  return (
    <Suspense fallback={<Loading fullPage tip="Loading..." />}>
      <Component />
    </Suspense>
  )
}

const routes: RouteObject[] = [
  // AXF-1013: the portal has no sign-in, and this route is what `/auth/login` now serves.
  //
  // There are no in-app callers left — all four (`DevAdminGuard`, `UserMenu`, `ForbiddenPage`,
  // `AuthCallbackPage`) were deleted by this ticket. The path is kept because it is the URL
  // staff have bookmarked and the one the old app redirected to, so it must explain itself
  // rather than 404. The guard renders `RestingPage` directly for a dead session; it does not
  // navigate here.
  {
    path: ROUTES.LOGIN,
    element: withSuspense(RestingPage),
  },
  // Protected routes — require isDevAdmin
  {
    path: '/',
    element: <DevAdminGuard />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.DASHBOARD} replace />,
          },
          {
            path: 'dashboard',
            element: withSuspense(DashboardPage),
          },
          {
            path: 'audit',
            element: withSuspense(AuditLogPage),
          },
          {
            path: 'presentation',
            element: withSuspense(PresetListPage),
          },
          {
            path: 'presentation/new',
            element: withSuspense(PresetEditorPage),
          },
          {
            path: 'presentation/:key',
            element: withSuspense(PresetEditorPage),
          },
          {
            path: 'health',
            element: withSuspense(HealthPage),
          },
          {
            path: 'workflows',
            element: withSuspense(WorkflowListPage),
          },
          {
            path: 'workflows/:id',
            element: withSuspense(WorkflowDetailsPage),
          },
          {
            path: 'executions',
            element: withSuspense(ExecutionListPage),
          },
          {
            path: 'executions/:id',
            element: withSuspense(ExecutionDetailsPage),
          },
          {
            path: 'monitoring',
            element: withSuspense(MonitoringPage),
          },
          {
            path: 'deployment',
            element: withSuspense(DeploymentPage),
          },
          {
            path: 'live-tests',
            element: withSuspense(LiveTestsPage),
          },
          {
            path: 'live-tests/history',
            element: withSuspense(LiveTestHistoryPage),
          },
          {
            path: 'live-tests/runs/:runId',
            element: withSuspense(LiveTestRunPage),
          },
          {
            path: 'connectors',
            element: withSuspense(ConnectorListPage),
          },
          {
            path: 'connectors/:id',
            element: withSuspense(ConnectorDetailsPage),
          },
          {
            path: 'groups',
            element: withSuspense(GroupListPage),
          },
          {
            path: 'groups/:groupId',
            element: withSuspense(GroupDetailsPage),
          },
          // AXF-1053: the admin Teams screen was replaced by read-only Groups (spec §8.8, PO 2026-09-15).
          // Old bookmarks land there instead of 404ing.
          {
            path: 'teams',
            element: <Navigate to={ROUTES.GROUPS} replace />,
          },
          {
            path: 'teams/:id',
            element: <Navigate to={ROUTES.GROUPS} replace />,
          },
          {
            path: 'tenants',
            element: withSuspense(TenantListPage),
          },
          {
            path: 'identity-switch-over',
            element: withSuspense(IdentitySwitchOverPage),
          },
          {
            path: 'person-export',
            element: withSuspense(PersonExportPage),
          },
          {
            path: 'users',
            element: withSuspense(UserListPage),
          },
          // AXF-1050 (D22): the legacy invitations system is gone; customers invite their own people.
          // Old bookmarks land on Users instead of 404ing.
          {
            path: 'invitations',
            element: <Navigate to={ROUTES.USERS} replace />,
          },
          {
            path: 'invitations/:id',
            element: <Navigate to={ROUTES.USERS} replace />,
          },
          {
            path: 'templates',
            element: withSuspense(TemplateListPage),
          },
          {
            path: 'templates/:id',
            element: withSuspense(TemplateDetailsPage),
          },
          {
            path: 'infra',
            element: withSuspense(InfraOverviewPage),
          },
          {
            path: 'infra/redis',
            element: withSuspense(RedisPage),
          },
          {
            path: 'infra/service-bus',
            element: withSuspense(ServiceBusPage),
          },
          {
            path: 'infra/db/:schema',
            element: withSuspense(DbBrowserPage),
          },
          {
            path: 'infra/db/:schema/:table',
            element: withSuspense(DbTablePage),
          },
          {
            path: 'whats-new',
            element: withSuspense(WhatsNewPage),
          },
        ],
      },
    ],
  },
  // 404
  {
    path: ROUTES.NOT_FOUND,
    element: withSuspense(NotFoundPage),
  },
  // Catch-all
  {
    path: '*',
    element: <Navigate to={ROUTES.NOT_FOUND} replace />,
  },
]

export const router = createBrowserRouter(routes)
export { routes }
